import { useState, useEffect, useCallback, useMemo } from "react";
import { api, formatCurrency } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Plus, FileText, Trash2, Edit, AlertTriangle, Euro, FolderOpen, Loader2, Tags,
} from "lucide-react";
import { toast } from "sonner";
import ContractCategoriesDialog from "./contracts/ContractCategoriesDialog";
import ContractsForecastChart from "./contracts/ContractsForecastChart";
import { colorOf } from "./contracts/contractColors";

const PERIOD_OPTIONS = [
  { value: "monthly", label: "Mensuel" },
  { value: "quarterly", label: "Trimestriel" },
  { value: "yearly", label: "Annuel" },
  { value: "one_time", label: "Ponctuel" },
];

const VAT_RATES = [
  { value: 0, label: "0 %" },
  { value: 2.1, label: "2,1 %" },
  { value: 5.5, label: "5,5 %" },
  { value: 10, label: "10 %" },
  { value: 20, label: "20 %" },
];

const defaultForm = {
  title: "",
  category: "autre",
  supplier: "",
  amount_input: 0,
  amount_input_type: "HT",
  vat_rate: 20,
  vat_deductible: false,
  period: "monthly",
  start_date: "",
  end_date: "",
  cancellation_date: "",
  notice_period_days: 60,
  alert_enabled: true,
  alert_email: "",
  notes: "",
};

const computeAmounts = (input, type, rate, deductible) => {
  const amt = parseFloat(input) || 0;
  const r = parseFloat(rate) || 0;
  let ht, ttc;
  if (type === "HT") {
    ht = amt;
    ttc = amt * (1 + r / 100);
  } else {
    ttc = amt;
    ht = r ? amt / (1 + r / 100) : amt;
  }
  return {
    ht: Math.round(ht * 100) / 100,
    ttc: Math.round(ttc * 100) / 100,
    vat: Math.round((ttc - ht) * 100) / 100,
    net: Math.round((deductible ? ht : ttc) * 100) / 100,
  };
};

export const ContractsTab = () => {
  const [data, setData] = useState({ items: [], total_monthly: 0, total_annual: 0, alerts_count: 0, totals_by_category: {} });
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState({ open: false, contract: null });
  const [catDialogOpen, setCatDialogOpen] = useState(false);
  const [form, setForm] = useState(defaultForm);
  const [saving, setSaving] = useState(false);
  const [sortBy, setSortBy] = useState("end_date");
  const [filterCategory, setFilterCategory] = useState("all");

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [contractsRes, catsRes] = await Promise.all([
        api.getContracts(),
        api.getContractCategories(),
      ]);
      setData(contractsRes.data);
      setCategories(catsRes.data.items || []);
      // Auto-migration: si on détecte des contrats legacy sans amount_input, on migre silencieusement
      const hasLegacy = (contractsRes.data.items || []).some(
        (c) => c.amount_input === undefined || c.amount_input === null,
      );
      if (hasLegacy) {
        try {
          await api.migrateExistingContracts();
          const refreshed = await api.getContracts();
          setData(refreshed.data);
        } catch (_e) { /* non bloquant */ }
      }
    } catch (_e) {
      toast.error("Erreur chargement contrats");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    if (dialog.open) {
      if (dialog.contract) {
        setForm({
          ...defaultForm,
          ...dialog.contract,
          amount_input: dialog.contract.amount_input ?? dialog.contract.amount ?? 0,
          amount_input_type: dialog.contract.amount_input_type || "HT",
          vat_rate: dialog.contract.vat_rate ?? 20,
          vat_deductible: !!dialog.contract.vat_deductible,
        });
      } else {
        setForm(defaultForm);
      }
    }
  }, [dialog]);

  const computed = useMemo(
    () => computeAmounts(form.amount_input, form.amount_input_type, form.vat_rate, form.vat_deductible),
    [form.amount_input, form.amount_input_type, form.vat_rate, form.vat_deductible],
  );

  const categoryByKey = useMemo(() => {
    const map = {};
    categories.forEach((c) => { map[c.key] = c; });
    return map;
  }, [categories]);

  const sortedTotalsByCategory = useMemo(() => {
    return Object.entries(data?.totals_by_category || {}).sort((a, b) => b[1] - a[1]);
  }, [data?.totals_by_category]);

  const handleSubmit = async () => {
    if (!form.title?.trim()) {
      toast.error("Titre obligatoire");
      return;
    }
    try {
      setSaving(true);
      const payload = {
        title: form.title.trim(),
        category: form.category || "autre",
        supplier: form.supplier?.trim() || null,
        amount_input: parseFloat(form.amount_input) || 0,
        amount_input_type: form.amount_input_type,
        vat_rate: parseFloat(form.vat_rate) || 0,
        vat_deductible: !!form.vat_deductible,
        period: form.period,
        notice_period_days: parseInt(form.notice_period_days, 10) || 60,
        start_date: form.start_date || null,
        end_date: form.end_date || null,
        cancellation_date: form.cancellation_date || null,
        alert_enabled: !!form.alert_enabled,
        alert_email: form.alert_email?.trim() || null,
        notes: form.notes?.trim() || null,
      };
      if (dialog.contract) {
        await api.updateContract(dialog.contract.id, payload);
        toast.success("Contrat mis à jour");
      } else {
        await api.createContract(payload);
        toast.success("Contrat créé");
      }
      setDialog({ open: false, contract: null });
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm("Supprimer ce contrat ?")) return;
    try {
      await api.deleteContract(id);
      toast.success("Contrat supprimé");
      fetchData();
    } catch (_e) {
      toast.error("Échec de la suppression");
    }
  };

  const filtered = useMemo(() => {
    const rows = filterCategory === "all"
      ? data.items
      : data.items.filter((c) => c.category === filterCategory);
    return [...rows].sort((a, b) => {
      if (sortBy === "monthly") return (b.monthly_amount || 0) - (a.monthly_amount || 0);
      if (sortBy === "annual") return (b.annual_amount || 0) - (a.annual_amount || 0);
      if (sortBy === "title") return (a.title || "").localeCompare(b.title || "", "fr");
      if (sortBy === "category") return (a.category || "").localeCompare(b.category || "", "fr");
      if (sortBy === "end_date") {
        const ad = a.end_date || "9999";
        const bd = b.end_date || "9999";
        return ad.localeCompare(bd);
      }
      return 0;
    });
  }, [data.items, filterCategory, sortBy]);

  if (loading) return <div className="flex items-center justify-center py-10"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  return (
    <div className="space-y-4">
      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <Card><CardContent className="pt-4">
          <div className="text-xs text-muted-foreground">Contrats actifs</div>
          <div className="text-2xl font-bold flex items-center gap-2"><FileText className="h-5 w-5 text-indigo-600" />{data.items.length}</div>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <div className="text-xs text-muted-foreground">Coût mensuel (net)</div>
          <div className="text-2xl font-bold text-indigo-700 flex items-center gap-2"><Euro className="h-5 w-5" />{formatCurrency(data.total_monthly)}</div>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <div className="text-xs text-muted-foreground">Coût annuel (net)</div>
          <div className="text-2xl font-bold text-indigo-700 flex items-center gap-2"><Euro className="h-5 w-5" />{formatCurrency(data.total_annual)}</div>
        </CardContent></Card>
        <Card className={data.alerts_count > 0 ? "border-amber-300" : ""}><CardContent className="pt-4">
          <div className="text-xs text-muted-foreground">Alertes préavis</div>
          <div className={`text-2xl font-bold flex items-center gap-2 ${data.alerts_count > 0 ? "text-amber-700" : "text-slate-500"}`}>
            <AlertTriangle className="h-5 w-5" />{data.alerts_count}
          </div>
        </CardContent></Card>
      </div>

      {/* Forecast chart with simulator */}
      <ContractsForecastChart contracts={data.items} />

      {/* Totaux par catégorie */}
      {sortedTotalsByCategory.length > 0 && (
        <Card><CardContent className="pt-4 space-y-2">
          <div className="flex items-center gap-2"><FolderOpen className="h-4 w-4 text-slate-600" /><span className="text-sm font-medium">Coût mensuel par catégorie</span></div>
          <div className="flex flex-wrap gap-2">
            {sortedTotalsByCategory.map(([cat, amt]) => {
              const c = categoryByKey[cat];
              return (
                <Badge key={cat} className={`${colorOf(c?.color).badge} border font-mono text-xs`}>
                  {c?.label || cat} : {formatCurrency(amt)}
                </Badge>
              );
            })}
          </div>
        </CardContent></Card>
      )}

      {/* Filtres + actions */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-2">
          <Label className="text-xs text-muted-foreground">Filtrer</Label>
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="w-[200px] h-9" data-testid="contract-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes catégories</SelectItem>
              {categories.map((o) => (
                <SelectItem key={o.id} value={o.key}>
                  <span className="inline-flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${colorOf(o.color).dot}`} />
                    {o.label}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-xs text-muted-foreground">Trier par</Label>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-[200px] h-9" data-testid="contract-sort">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="end_date">Date de fin (proche)</SelectItem>
              <SelectItem value="monthly">Coût mensuel décroissant</SelectItem>
              <SelectItem value="annual">Coût annuel décroissant</SelectItem>
              <SelectItem value="title">Nom (A→Z)</SelectItem>
              <SelectItem value="category">Catégorie (A→Z)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1" />
        <Button variant="outline" onClick={() => setCatDialogOpen(true)} data-testid="manage-categories-btn">
          <Tags className="h-4 w-4 mr-1" />Catégories
        </Button>
        <Button onClick={() => setDialog({ open: true, contract: null })} data-testid="add-contract-btn">
          <Plus className="h-4 w-4 mr-1" />Nouveau contrat
        </Button>
      </div>

      {/* Liste des contrats */}
      <Card>
        <CardContent className="pt-4">
          {filtered.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">Aucun contrat enregistré. Cliquez sur "Nouveau contrat".</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Titre</TableHead>
                  <TableHead>Catégorie</TableHead>
                  <TableHead>Fournisseur</TableHead>
                  <TableHead className="text-right">HT</TableHead>
                  <TableHead className="text-right">TTC</TableHead>
                  <TableHead className="text-right">/mois</TableHead>
                  <TableHead>Fin</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((c) => {
                  const cat = categoryByKey[c.category];
                  return (
                    <TableRow key={c.id} data-testid={`contract-row-${c.id}`} className={c.alert_active ? "bg-amber-50" : ""}>
                      <TableCell>
                        <div className="font-medium">{c.title}</div>
                        {c.notes && <div className="text-xs text-muted-foreground truncate max-w-xs">{c.notes}</div>}
                      </TableCell>
                      <TableCell>
                        <Badge className={`${colorOf(cat?.color).badge} border`}>
                          {cat?.label || c.category}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">{c.supplier || "—"}</TableCell>
                      <TableCell className="text-right font-mono text-xs">
                        {formatCurrency(c.amount_ht ?? 0)}
                      </TableCell>
                      <TableCell className="text-right font-mono text-xs">
                        {formatCurrency(c.amount_ttc ?? c.amount ?? 0)}
                        <div className="text-[10px] text-muted-foreground">
                          {PERIOD_OPTIONS.find((p) => p.value === c.period)?.label}
                          {c.vat_deductible && " · TVA déd."}
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-mono text-indigo-700">{formatCurrency(c.monthly_amount)}</TableCell>
                      <TableCell>
                        {c.end_date ? (
                          <div className="flex flex-col">
                            <span className="text-sm">{c.end_date}</span>
                            {c.days_until_end !== null && (
                              <Badge
                                variant="outline"
                                className={`text-[10px] w-fit ${
                                  c.alert_active ? "border-amber-500 text-amber-800 bg-amber-100" :
                                  c.days_until_end < 0 ? "border-red-500 text-red-800" :
                                  "border-slate-300"
                                }`}
                              >
                                {c.days_until_end < 0 ? `Terminé il y a ${-c.days_until_end} j` : `J-${c.days_until_end}`}
                                {c.alert_active && <AlertTriangle className="h-2.5 w-2.5 ml-1" />}
                              </Badge>
                            )}
                          </div>
                        ) : <span className="text-muted-foreground text-xs">—</span>}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" onClick={() => setDialog({ open: true, contract: c })} data-testid={`edit-contract-${c.id}`}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(c.id)} data-testid={`delete-contract-${c.id}`}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Dialog Catégories */}
      <ContractCategoriesDialog
        open={catDialogOpen}
        onOpenChange={setCatDialogOpen}
        onChanged={fetchData}
      />

      {/* Dialog Contrat */}
      <Dialog open={dialog.open} onOpenChange={(v) => !v && setDialog({ open: false, contract: null })}>
        <DialogContent className="sm:max-w-[680px] max-h-[90vh] overflow-y-auto" data-testid="contract-dialog">
          <DialogHeader>
            <DialogTitle>{dialog.contract ? "Modifier le contrat" : "Nouveau contrat / charge fixe"}</DialogTitle>
            <DialogDescription>
              Saisissez le montant en HT ou TTC, indiquez la TVA et si elle est déductible. Une alerte email sera envoyée avant la fin du contrat selon le préavis.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="md:col-span-2">
              <Label>Titre *</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Ex: Assurance flotte véhicules Groupama" data-testid="contract-title" />
            </div>
            <div>
              <Label>Catégorie</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="contract-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {categories.map((o) => (
                    <SelectItem key={o.id} value={o.key}>
                      <span className="inline-flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${colorOf(o.color).dot}`} />
                        {o.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Fournisseur</Label>
              <Input value={form.supplier} onChange={(e) => setForm({ ...form, supplier: e.target.value })} placeholder="Ex: Orange, Groupama" />
            </div>

            {/* Montant HT/TTC + TVA */}
            <div className="md:col-span-2 rounded-lg border bg-slate-50 p-3 space-y-3">
              <Label className="text-sm font-medium">Montant et TVA</Label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">Type de saisie</Label>
                  <Select value={form.amount_input_type} onValueChange={(v) => setForm({ ...form, amount_input_type: v })}>
                    <SelectTrigger data-testid="contract-amount-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="HT">HT (hors taxes)</SelectItem>
                      <SelectItem value="TTC">TTC (toutes taxes)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Montant {form.amount_input_type} (€)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={form.amount_input}
                    onChange={(e) => setForm({ ...form, amount_input: e.target.value })}
                    data-testid="contract-amount"
                  />
                </div>
                <div>
                  <Label className="text-xs">Taux de TVA</Label>
                  <Select value={String(form.vat_rate)} onValueChange={(v) => setForm({ ...form, vat_rate: parseFloat(v) })}>
                    <SelectTrigger data-testid="contract-vat-rate"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {VAT_RATES.map((r) => (
                        <SelectItem key={r.value} value={String(r.value)}>{r.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex items-center gap-2 pt-1">
                <Checkbox
                  id="vat-deductible"
                  checked={form.vat_deductible}
                  onCheckedChange={(v) => setForm({ ...form, vat_deductible: !!v })}
                  data-testid="contract-vat-deductible"
                />
                <Label htmlFor="vat-deductible" className="cursor-pointer text-sm">
                  TVA déductible (récupérable) — le coût net est calculé en HT
                </Label>
              </div>
              {/* Live preview */}
              <div className="grid grid-cols-3 gap-2 text-center pt-1">
                <div className="rounded-md bg-white border p-2">
                  <div className="text-[10px] text-muted-foreground">HT</div>
                  <div className="font-mono text-sm">{formatCurrency(computed.ht)}</div>
                </div>
                <div className="rounded-md bg-white border p-2">
                  <div className="text-[10px] text-muted-foreground">TVA ({form.vat_rate}%)</div>
                  <div className="font-mono text-sm">{formatCurrency(computed.vat)}</div>
                </div>
                <div className="rounded-md bg-indigo-50 border border-indigo-200 p-2">
                  <div className="text-[10px] text-indigo-700">{form.vat_deductible ? "Coût net (HT)" : "Coût net (TTC)"}</div>
                  <div className="font-mono text-sm font-semibold text-indigo-700" data-testid="contract-net-cost">
                    {formatCurrency(computed.net)}
                  </div>
                </div>
              </div>
            </div>

            <div>
              <Label>Périodicité</Label>
              <Select value={form.period} onValueChange={(v) => setForm({ ...form, period: v })}>
                <SelectTrigger data-testid="contract-period"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PERIOD_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Préavis (jours)</Label>
              <Input type="number" value={form.notice_period_days} onChange={(e) => setForm({ ...form, notice_period_days: e.target.value })} placeholder="60" />
            </div>
            <div>
              <Label>Date de début</Label>
              <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
            </div>
            <div>
              <Label>Date de fin</Label>
              <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="contract-end-date" />
            </div>
            <div className="md:col-span-2">
              <Label>Date de résiliation (optionnel)</Label>
              <Input type="date" value={form.cancellation_date} onChange={(e) => setForm({ ...form, cancellation_date: e.target.value })} />
            </div>
            <div className="md:col-span-2 flex items-center gap-3 pt-2">
              <Checkbox
                id="alert-enabled"
                checked={form.alert_enabled}
                onCheckedChange={(v) => setForm({ ...form, alert_enabled: !!v })}
                data-testid="contract-alert-enabled"
              />
              <Label htmlFor="alert-enabled" className="cursor-pointer text-sm">
                Alerter par email {form.notice_period_days} jours avant la fin du contrat
              </Label>
            </div>
            {form.alert_enabled && (
              <div className="md:col-span-2">
                <Label>Email destinataire (optionnel — par défaut votre email)</Label>
                <Input type="email" value={form.alert_email} onChange={(e) => setForm({ ...form, alert_email: e.target.value })} placeholder="contact@exemple.fr" />
              </div>
            )}
            <div className="md:col-span-2">
              <Label>Notes</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Clause, numéro de police, contact résiliation…" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialog({ open: false, contract: null })} disabled={saving}>Annuler</Button>
            <Button onClick={handleSubmit} disabled={saving} data-testid="save-contract-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enregistrer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ContractsTab;
